/* eslint-disable no-unused-vars */
import { Typography } from "@mui/material";
import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
import { Box } from "@mui/material";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  cleanPayload,
  normalizeFormErrors,
  transformObjectValues,
} from "../../../../utils/objectUtils";
import MainLayout from "../../../../layouts/MainLayout";
import PageHeader from "../../../../components/PageHeader";
import Form from "../../../../components/forms/Form";
import SectionHeader from "../../../../components/forms/SectionHeader";
import SimpleBody from "../../../../components/forms/SimpleBody";
import useCreateMovement from "../hooks/useCreateMovement";
import { usePermissions } from "../../../../hooks/usePermissions";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import useLoader from "../../../../hooks/useLoader";
import { entities } from "../../../../constants/entities";
import useBaseService from "../../../../hooks/services/useBaseService";
import { useOrgan } from "../../../../hooks/useOrgan";
import useNotification from "../../../../hooks/useNotification";
import { useVolume } from "../hooks/useVolume";
import { Biped } from "../components/biped/Biped";
import { Volume } from "../components/volumes/Volume";
import { GenericStatusEnumById } from "../../../../enums/generic-status-enum";
import useQueryWithLoader from "../../../../hooks/useQueryWithLoader";
import { transformFiltersForUrl } from "../../../../utils/transformFiltersForUrl";
import { mapMovementDataToVolumeForm } from "../utils/mapMovementDataToVolumeForm";
import { EditVolumeModal } from "../components/volumes/EditVolumeModal";
import ListHeader from "../../../../components/ListHeader";
import {
  Alert,
  Chip,
  CircularProgress,
  IconButton,
  Tooltip,
} from "@mui/material";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faEdit,
  faTrash,
  faCircleExclamation,
} from "@fortawesome/free-solid-svg-icons";
import useAction from "../../../../hooks/useAction";
import ConfirmationModal from "../../../../components/modals/ConfirmationModal";
import Button from "../../../../components/Button";

dayjs.extend(customParseFormat);

const AddMovementItemPage = () => {
  const { movementId, itemId } = useParams();
  const navigate = useNavigate();

  const { canAccess } = usePermissions();
  const queryClient = useQueryClient();
  const { showLoader, hideLoader } = useLoader();
  const { showNotification } = useNotification();

  const {
    post: baseServicePost,
    get: baseServiceGet,
    put: baseServicePut,
    del: baseServiceDelete,
    setFormErrors,
  } = useBaseService(navigate);

  const {
    formData,
    handleChange,
    formErrors,
    viewTable,
    setViewTable,
    getOptions,
    getSelectedValue,
    formFields,
    addFieldsInData,
    equipments,
    products,
    setProducts,
    volumes,
    setVolumes,
    assets,
    inputs,
    setInputs,
    setAssets,
    control,
    setValue,
    isLoading,
    isDebouncePending,
    debouncedAddEquipment,
    flushAddEquipment,
    handleRemoveItem,
    handleRemoveBipedItem,
    firstInputRef,
    inputRefs,
    debouncedAddProduct,
    serviceOrderItemsData,
  } = useCreateMovement();

  // === ESTADOS PARA O MODAL DE EDIÇÃO DE VOLUMES ===
  const [isEditVolumeModalOpen, setIsEditVolumeModalOpen] = useState(false);
  const [editVolumeData, setEditVolumeData] = useState({
    bipeds: {},
    initialVolumes: [],
  });

  // === BUSCA ITENS EXISTENTES DO MOVIMENTO ===
  const {
    data: movementItemEquipmentsResponseData,
    isLoading: isLoadingMovementEquipments,
  } = useQueryWithLoader({
    queryKey: ["movementItemEquipments", movementId],
    queryFn: async () => {
      const filtersTranformed = transformFiltersForUrl({ deleted_at: false });
      const params = {
        filters: filtersTranformed,
        page: 1,
        perPage: 9999999999,
        lean: true,
      };
      const response = await baseServiceGet(
        entities.movements.itemEquipments.get(movementId),
        params,
      );
      return response.result;
    },
    refetchOnWindowFocus: true,
    showGlobalLoader: false,
    select: (data) => {
      return {
        movementsEquipments: data.data
          .map((item) => ({
            id: item.id,
            equipment_id: item.equipment?.id,
            equipment_number: String(item.equipment?.number ?? "-"),
            equipment_name: item.equipment?.name ?? "-",
            extra_id: item.extra_id ?? null,
            equipment: item.extra_id
              ? `(KIT) ${item.equipment?.number} - ${item.equipment?.name}`
              : `${item.equipment?.number} - ${item.equipment?.name}`,
            contract_item: item.order_item?.contract_item?.alias ?? "-",
            movement_type: item.type?.name ?? "-",
            status: GenericStatusEnumById[item.status_id] ?? "-",
            isDeleted: !!item.deleted_at,
            return_equipment: [
              {
                number: item.order_item?.equipment?.number ?? "-",
                name: item.order_item?.equipment?.name ?? "-",
                status:
                  GenericStatusEnumById[item.order_item?.status_id] ?? "-",
                contract_item: item.order_item?.contract_item?.alias ?? "-",
                shipment_item_status: item.shipment_item?.status_id ?? false,
              },
            ],
          }))
          .sort((a, b) => {
            if (!!a.extra_id !== !!b.extra_id) return a.extra_id ? 1 : -1;
            const nameCompare = a?.equipment_name?.localeCompare(
              b?.equipment_name,
            );
            if (nameCompare !== 0) return nameCompare;
            return a?.equipment_number?.localeCompare(
              b?.equipment_number,
              undefined,
              { numeric: true },
            );
          }),
      };
    },
  });

  const {
    data: movementItemProductsResponseData,
    isLoading: isLoadingMovementProducts,
  } = useQueryWithLoader({
    queryKey: ["movementItemProducts", movementId],
    queryFn: async () => {
      const filtersTranformed = transformFiltersForUrl({ deleted_at: false });
      const params = {
        filters: filtersTranformed,
        page: 1,
        perPage: 9999999999,
      };
      const response = await baseServiceGet(
        entities.movements.itemProducts.get(movementId),
        params,
      );
      return response.result;
    },
    refetchOnWindowFocus: true,
    showGlobalLoader: false,
    select: (data) => {
      return {
        movementsProducts: data.data
          .map((item) => {
            const shippedQuantity =
              item.movement?.volumes?.reduce((acc, volume) => {
                const itemsInShipment =
                  volume.items?.filter(
                    (vItem) =>
                      vItem.product_item_id === item.product_item_id &&
                      vItem.shipment_item_id !== null,
                  ) || [];
                const volumeSum = itemsInShipment.reduce(
                  (sum, vItem) => sum + (parseFloat(vItem.quantity) || 1),
                  0,
                );
                return acc + volumeSum;
              }, 0) || 0;

            return {
              id: item.id,
              product_item_id: item.product_item?.id,
              product:
                `${item.product_item?.product?.name} | ${item.product_item?.category?.name} | ${item.product_item?.condition?.name}` ??
                "-",
              address:
                `${item.product_item?.address?.addressable?.alias} - ${item.product_item?.address?.formatted_address}` ??
                "-",
              location: item.product_item?.location?.formatted_location ?? "-",
              quantity_product: item.total_product_quantity ?? 0,
              progress_label: `${shippedQuantity ?? "-"} / ${item.total_product_quantity ?? "-"}`,
              movement_type: item.type?.name ?? "-",
              status: GenericStatusEnumById[item.status?.id] ?? "-",
              isDeleted: !!item.deleted_at,
            };
          })
          .sort((a, b) => a.product.localeCompare(b.product)),
      };
    },
  });

  const {
    data: movementVolumesResponseData,
    isLoading: isLoadingMovementVolumes,
  } = useQueryWithLoader({
    queryKey: ["movementVolumes", movementId],
    queryFn: async () => {
      const params = {
        page: 1,
        perPage: 9999999999,
        sort: { column: "id", order: "desc" },
        lean: true,
      };
      const response = await baseServiceGet(
        entities.movements.volumes.get(movementId),
        params,
      );
      return response.result;
    },
    keepPreviousData: true,
    refetchOnMount: true,
    refetchOnWindowFocus: true,
    showGlobalLoader: false,
    select: (data) => {
      return {
        movementsVolumes: data?.data.map((item) => ({
          id: item.id,
          code: item.code,
          name: item.name,
          shipped: item.shipped ? "Entregue" : "Aguardando Carregamento",
          status: GenericStatusEnumById[item.status] ?? "-",
          hasShipment: !!item.shipment,
          shipmentId: item.shipment?.id ?? null,
          items: item?.items.map((i) => {
            const isProduct = !!i.product_item_id;
            return {
              id: i.id,
              movement_item_id: i.movement_item_id,
              type: isProduct ? "product" : "equipment",
              equipment_id: i.equipment_id ?? null,
              product_item_id: i.product_item_id ?? null,
              product_id: i.product_item?.product_id ?? null,
              name: isProduct
                ? `${i.product_item?.product?.name} | ${i.product_item?.category?.name} | ${i.product_item?.condition?.name}`
                : `${i.equipment?.number} - ${i.equipment?.name}`,
              address: isProduct
                ? `${i.product_item?.address?.addressable?.alias} - ${i.product_item?.address?.formatted_address}`
                : `${i.equipment?.product_item?.address?.addressable?.alias} - ${i.equipment?.product_item?.address?.formatted_address}`,
              location: isProduct
                ? i.product_item?.location?.formatted_location
                : i.equipment?.product_item?.location?.formatted_location,
              quantity: i.quantity ?? 1,
              ...(i.equipment && { equipment_number: i.equipment?.number }),
              ...(i.equipment && { equipment_name: i.equipment?.name }),
              ...(i.product_item?.product && {
                product_name: i.product_item?.product?.name,
              }),
              ...(i.product_item?.category && {
                product_category: i.product_item?.category?.name,
              }),
              ...(i.product_item?.condition && {
                product_condition: i.product_item?.condition?.name,
              }),
            };
          }),
          created_at: item.created_at,
        })),
      };
    },
  });

  const { movementsVolumes = [] } = movementVolumesResponseData || {};
  const { movementsProducts = [] } = movementItemProductsResponseData || {};
  const { movementsEquipments = [] } = movementItemEquipmentsResponseData || {};

  const isLoadingMovementItems =
    isLoadingMovementEquipments ||
    isLoadingMovementProducts ||
    isLoadingMovementVolumes;

  // Inicializa assets, inputs e volumes com dados existentes
  useEffect(() => {
    if (
      movementsEquipments.length === 0 &&
      movementsProducts.length === 0 &&
      movementsVolumes.length === 0
    ) {
      return;
    }

    // 1. Popular `assets` com equipamentos existentes (marcados como NÃO novos)
    const newAssets = {};
    movementsEquipments.forEach((eq) => {
      const identify = `eq_${eq.id}`;
      newAssets[identify] = {
        id: eq.equipment_id,
        identify,
        alias: eq.equipment_name,
        isNew: false,
        equipment: {
          id: eq.equipment_id,
          number: eq.equipment_number,
          name: eq.equipment_name,
        },
      };
    });

    // 2. Popular `inputs` com produtos existentes (marcados como NÃO novos)
    const newInputs = {};
    movementsProducts.forEach((prod) => {
      const identify = `prod_${prod.id}`;
      newInputs[identify] = {
        id: prod.product_item_id,
        identify,
        alias: prod.product,
        isNew: false,
        product: {
          name: prod.product,
        },
        quantity: prod.quantity_product,
        // Metadados necessários caso precise ser enviado
        service_order_item_id: prod.service_order_item_id,
        movement_type_id: prod.movement_type_id,
      };
    });

    // 3. Atualiza estados (só se não estiverem populados)
    if (Object.keys(newAssets).length > 0 && Object.keys(assets).length === 0) {
      setAssets(newAssets);
    }
    if (Object.keys(newInputs).length > 0 && Object.keys(inputs).length === 0) {
      setInputs(newInputs);
    }

    // 4. Popular `volumes` com volumes existentes (mapeados)
    if (volumes.length === 0 && movementsVolumes.length > 0) {
      const { initialVolumes } = mapMovementDataToVolumeForm(
        movementsEquipments,
        movementsProducts,
        movementsVolumes,
      );
      setVolumes(initialVolumes);
    }
  }, [
    movementsEquipments,
    movementsProducts,
    movementsVolumes,
    assets,
    inputs,
    volumes,
    setAssets,
    setInputs,
    setVolumes,
  ]);

  // === MUTAÇÃO: Criar novos itens do movimento ===
  const createMovementItemMutation = useMutation({
    mutationFn: async (payload) => {
      showLoader();
      const response = await baseServicePost(
        entities.movements.manyItems.create(movementId),
        payload,
      );
      return response;
    },
    onSuccess: (response) => {
      if (response && response?.status === 201) {
        queryClient.invalidateQueries({ queryKey: ["movementItemEquipments"] });
        queryClient.invalidateQueries({ queryKey: ["movementItemProducts"] });
        queryClient.invalidateQueries({ queryKey: ["movementVolumes"] });
        queryClient.invalidateQueries({ queryKey: ["movementDetails"] });
        queryClient.invalidateQueries({ queryKey: ["osDetails"] });
        queryClient.invalidateQueries({ queryKey: ["movements"] });
      }
    },
    onError: (error) => {
      console.error("error", error);
      if (error?.response?.data?.errors) {
        setFormErrors(error.response.data.errors);
      }
    },
    onSettled: () => {
      hideLoader();
    },
  });

  // === MUTAÇÃO: Sincronizar volumes ===
  const syncVolumesMutation = useMutation({
    mutationFn: async (data) => {
      showLoader();
      const response = await baseServicePut(
        entities.movements.volumesUpdate(movementId),
        data,
      );
      return response;
    },
    onSuccess: (response) => {
      if (response?.status === 200) {
        queryClient.invalidateQueries({ queryKey: ["movementItemEquipments"] });
        queryClient.invalidateQueries({ queryKey: ["movementItemProducts"] });
        queryClient.invalidateQueries({ queryKey: ["movementVolumes"] });
        queryClient.invalidateQueries({ queryKey: ["movementDetails"] });
        queryClient.invalidateQueries({ queryKey: ["osDetails"] });
        queryClient.invalidateQueries({ queryKey: ["movements"] });
      }
    },
    onError: (error) => {
      console.error("Erro ao sincronizar volumes:", error);
      showNotification("error", "Erro ao sincronizar volumes.");
    },
    onSettled: () => {
      hideLoader();
    },
  });

  const { selectedOrgan } = useOrgan();

  const handleSubmit = useCallback(async () => {
    try {
      const assetsArray = Object.values(assets).filter((a) => a.isNew === true);
      const inputsArray = Object.values(inputs).filter((i) => i.isNew === true);

      const transformedData = {
        movement: {
          ...transformObjectValues(formData.movement),
          movement_id: movementId,
          organization_id: selectedOrgan?.id,
          service_order_id:
            formData.movement?.service_order_id?.value ||
            formData.movement?.service_order_id,
        },
        items: [
          ...assetsArray.map((item) => ({
            service_order_item_id: item.service_order_item_id,
            movement_type_id: item.movement_type_id,
            alias: item.alias,
            contract_item_id: item.contract_item_id,
            exchange_type_id: item.exchange_type_id,
            equipment_id: item.id,
            is_extra: item.is_extra,
            ...(item.is_extra && { extra_id: item.extra_id }),
          })),
          ...inputsArray.map((input) => ({
            service_order_item_id: input.service_order_item_id,
            movement_type_id: input.movement_type_id,
            product_item_id: input.id,
            is_extra: false,
            product_id: input?.service_order_item?.product_id,
          })),
        ],
      };

      const cleanedData = cleanPayload({
        movement: { ...transformedData.movement },
        items: transformedData.items,
      });

      // Validação defensiva: verifica se todos os itens têm os campos obrigatórios
      const invalidItems = cleanedData.items.filter(
        (item) => !item.service_order_item_id || !item.movement_type_id,
      );

      if (invalidItems.length > 0) {
        console.error("Itens inválidos detectados:", invalidItems);
        showNotification(
          "error",
          "Alguns itens estão com dados incompletos. Verifique o console.",
        );
        return;
      }

      const hasAnyEquipment = cleanedData.items?.some(
        (item) => item.equipment_id,
      );
      const hasAnyProduct = cleanedData.items?.some(
        (item) => item.product_item_id,
      );

      // Validação de segurança básica
      if (!hasAnyEquipment && !hasAnyProduct) {
        showNotification(
          "warning",
          "Nenhum item adicionado. Adicione pelo menos um equipamento ou insumo.",
        );
        return;
      }

      // ========================================================
      // Bloquear submit se itens estiverem soltos fora dos volumes
      // ========================================================
      const allocatedEquipmentIdentifies = new Set();
      const allocatedProductQuantities = {};

      // Mapeia o que foi alocado para dentro do estado de volumes original
      volumes
        ?.filter((v) => v.items?.length > 0)
        .forEach((volume) => {
          volume.items.forEach((vItem) => {
            if (vItem.type === "equipment") {
              allocatedEquipmentIdentifies.add(vItem.identify);
            } else if (vItem.type === "product") {
              allocatedProductQuantities[vItem.product_item_id] =
                (allocatedProductQuantities[vItem.product_item_id] || 0) +
                vItem.quantity;
            }
          });
        });

      const hasItemsOutsideVolumes =
        assetsArray.some(
          (asset) => !allocatedEquipmentIdentifies.has(asset.identify),
        ) ||
        inputsArray.some((input) => {
          const allocated = allocatedProductQuantities[input.id] || 0;

          return allocated === 0;
        });

      if (hasItemsOutsideVolumes) {
        showNotification(
          "warning",
          "Todos os equipamentos e insumos devem estar distribuídos em volumes antes de finalizar o movimento.",
        );
        return;
      }

      await createMovementItemMutation.mutateAsync(cleanedData);

      if (volumes.length > 0) {
        await syncVolumesMutation.mutateAsync({
          movementId,
          volumes,
        });
      }

      showNotification("success", "Itens adicionados com sucesso!");
      navigate(`/aluguel/movimentos/detalhes/${movementId}`);
    } catch (error) {
      console.error("Erro ao adicionar item:", error);
    }
  }, [
    assets,
    inputs,
    formData.movement,
    movementId,
    selectedOrgan?.id,
    volumes,
    createMovementItemMutation,
    syncVolumesMutation,
    showNotification,
    navigate,
  ]);

  const handleBack = useCallback(() => {
    navigate(`/aluguel/movimentos/detalhes/${movementId}`);
  }, [navigate, movementId]);

  const columns = [
    "Item da Requisição",
    "Quantidade Requisitada",
    "Tombo/Código",
    "Nome",
    "Ações",
  ];

  const data = useMemo(
    () => [...equipments, ...products],
    [equipments, products],
  );

  const assetsDataState = useMemo(
    () => ({
      ...assets,
      ...inputs,
    }),
    [assets, inputs],
  );

  const hasAnyEditableVolume = movementsVolumes.some((v) => !v.hasShipment);

  const {
    newVolume,
    setNewVolume,
    openVolumesModal,
    handleOpenVolumesModal,
    handleCloseVolumesModal,
    handleRemoveVolume,
    handleEditVolume,
    getTotalAlreadyAllocated,
  } = useVolume({
    data,
    setVolumes,
    setProducts,
    volumes,
    assetsData: assetsDataState,
  });

  // === COLUNAS DA TABELA DE VOLUMES ===
  const volumesColumns = [
    { key: "name", label: "Volume" },
    { key: "code", label: "Código" },
    { key: "status", label: "Status" },
    {
      key: "hasShipment",
      label: "Carregamento",
      align: "center",
      render: (value, volume) =>
        volume.hasShipment ? (
          <Chip
            label={`#${volume.shipmentId}`}
            size="small"
            color="warning"
            icon={<FontAwesomeIcon icon={faCircleExclamation} />}
            sx={{ fontWeight: "bold" }}
          />
        ) : (
          <Chip label="Livre" size="small" color="success" variant="filled" />
        ),
    },
  ];

  return (
    <MainLayout selectedCompany="ALUCOM">
      <PageHeader
        title={`Adicionar Itens ao Movimento #${movementId}`}
        showBackButton={true}
        backUrl={`/aluguel/movimentos/detalhes/${movementId}`}
      />

      <div className="container-fluid p-1">
        <Form
          initialFormData={formData}
          onSubmit={handleSubmit}
          textSubmit="Adicionar Itens"
          textLoadingSubmit="Adicionando..."
          handleBack={handleBack}
          isLoading={
            createMovementItemMutation.isPending ||
            syncVolumesMutation.isPending
          }>
          {() => {
            const renderedSections = new Set();
            return formFields.map((section, index) => {
              const sectionType = section.section.split(" - ")[0];
              const showHeader = !renderedSections.has(sectionType);

              if (showHeader) {
                renderedSections.add(sectionType);
              }

              return (
                <div key={section.section + " " + index}>
                  {showHeader && (
                    <SectionHeader
                      section={section.section}
                      viewTable={viewTable}
                      setViewTable={setViewTable}
                      addFieldsInData={addFieldsInData}
                    />
                  )}
                  <SimpleBody
                    fields={section.fields}
                    formErrors={normalizeFormErrors(formErrors)}
                    formData={formData}
                    handleFieldChange={handleChange}
                    getOptions={getOptions}
                    getSelectedValue={getSelectedValue}
                  />

                  {formData?.movement?.service_order_id && (
                    <Box sx={{ my: 4, gap: 2 }}>
                      {isLoadingMovementItems && (
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 1,
                            mb: 2,
                          }}>
                          <CircularProgress size={18} />
                          <Typography variant="body2" color="text.secondary">
                            Carregando itens e volumes do movimento...
                          </Typography>
                        </Box>
                      )}

                      {/* BIPAGEM E VOLUMES NOVOS */}
                      <Volume
                        bipeds={assetsDataState}
                        volumes={volumes}
                        setVolumes={setVolumes}
                        newVolume={newVolume}
                        setNewVolume={setNewVolume}
                        openVolumesModal={openVolumesModal}
                        handleOpenVolumesModal={handleOpenVolumesModal}
                        handleCloseVolumesModal={handleCloseVolumesModal}
                        handleRemoveVolume={handleRemoveVolume}
                        handleRemoveItem={handleRemoveItem}
                        handleEditVolume={handleEditVolume}
                      />

                      <Biped
                        data={data}
                        columns={columns}
                        assets={assets}
                        inputs={inputs}
                        volumes={volumes}
                        getTotalAlreadyAllocated={getTotalAlreadyAllocated}
                        control={control}
                        firstInputRef={firstInputRef}
                        inputRefs={inputRefs}
                        isLoading={isLoading}
                        isDebouncePending={isDebouncePending}
                        handleRemoveItem={handleRemoveItem}
                        debouncedAddEquipment={debouncedAddEquipment}
                        flushAddEquipment={flushAddEquipment}
                        debouncedAddProduct={debouncedAddProduct}
                        handleRemoveBipedItem={handleRemoveBipedItem}
                        assetsData={assetsDataState}
                        setProducts={setProducts}
                        setValue={setValue}
                        serviceOrderId={formData?.movement?.service_order_id?.value || formData?.movement?.service_order_id}
                        serviceOrderItemsData={serviceOrderItemsData}
                      />
                    </Box>
                  )}
                </div>
              );
            });
          }}
        </Form>
      </div>
    </MainLayout>
  );
};

export default AddMovementItemPage;
